import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { LinkedZoneDoc } from '@ledium/types';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Chip, CircularProgress, Popover, Typography } from '@mui/material';

import type { DashboardProjectItem } from './DashboardProject';

interface DashboardLinkPopoverProps {
    project: DashboardProjectItem;
}

function statusChip(disabledReason: LinkedZoneDoc['disabledReason']) {
    if (disabledReason === 'mapping_error') {
        return <Chip size="small" color="warning" label={i18n('linked_project_status_mapping_error') as string} />;
    }
    if (disabledReason === 'user') {
        return <Chip size="small" color="error" variant="outlined" label={i18n('linked_project_status_disabled') as string} />;
    }
    return <Chip size="small" color="success" label={i18n('linked_project_status_enabled') as string} />;
}

function DashboardLinkPopover({ project }: DashboardLinkPopoverProps) {
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const [links, setLinks] = React.useState<LinkedZoneDoc[] | null>(null);
    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (!project.zoneId) {
            return;
        }
        lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${project.zoneId}`).then((data) => {
            setLinks((data ?? []).filter((l) => l.type === 'mirror'));
        });
    }, [project.zoneId]);

    const hasError = (project.mirrorLinkErrorCount ?? 0) > 0;
    let iconColor = 'success.main';
    if (hasError) {
        iconColor = 'error.main';
    } else if (project.linkAllDisabled) {
        iconColor = 'text.disabled';
    }

    return (
        <>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
            >
                <LinkIcon sx={{ color: iconColor, fontSize: '1.75rem' }} />
                {links !== null && links.length > 0 && (
                    <Typography variant="body2" sx={{ color: iconColor, fontWeight: 500 }}>
                        {i18n('linked_project_mirror_badge', links.length)}
                    </Typography>
                )}
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
            >
                <Box sx={{ p: 2, minWidth: 240 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {i18n('linked_project_mirror_description')}
                    </Typography>
                    {links === null ? (
                        <CircularProgress size={20} />
                    ) : (
                        links.map((link) => (
                            <Box key={link._id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="body2">
                                        <strong>{link.name}</strong>
                                    </Typography>
                                    {statusChip(link.disabledReason)}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {i18n('dmx_channel') as string}: {link.channelMap.map((e) => e.dmxAddress).join(', ')}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </Popover>
        </>
    );
}

export default DashboardLinkPopover;
